import { useQuery } from '@tanstack/react-query';

import { ImpersonationBroker, type IPagedCollection } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import type { IFormFieldOption } from '../../viewModels/form';

/** One choice for an Option / MultipleOptions lead attribute. */
export type LeadAttributeOption = {
	id: string;
	label: string;
	/** Option color, when the CRM property defines one. */
	hexColor?: string;
};

export type UseLeadAttributeOptionsResult = {
	options: LeadAttributeOption[];
	isLoading: boolean;
	error: Error | null;
};

export function useLeadAttributeOptions(
	companyId: string | undefined,
	fieldId: string | undefined,
	open: boolean
): UseLeadAttributeOptionsResult {
	const userSession = useUserSession();

	const query = useQuery<LeadAttributeOption[], Error>({
		queryKey: ['focused-view', 'lead-attributes', 'field-options', companyId, fieldId] as const,
		enabled: !!companyId && !!fieldId && open,
		queryFn: async () => {
			const response = await userSession.webServiceHelper.callAsync<IPagedCollection<IFormFieldOption>>(
				ImpersonationBroker.composeApiUrl({ urlPath: `lead/${companyId}/Lead/Field/${fieldId}` }),
				'GET'
			);
			return (response.values ?? []).map(option => ({
				id: option.id ?? '',
				label: option.label ?? '',
				hexColor: option.hexColor,
			}));
		},
		// Toast fetch failures — otherwise a failed options fetch silently renders "No options" as if the field had none.
		meta: { toastErrorMessage: 'Failed to load field options' },
	});

	return {
		options: query.data ?? [],
		isLoading: query.isLoading,
		error: query.error ?? null,
	};
}
